export const isTest = () => process?.env?.NODE_ENV === 'test';

/**
 * Are we running in a browser page?
 *
 * @returns true iff the current context has access to `window`, `document`, etc.
 */
export const isMainThread = (() => {
  try {
    return (
      isTest() || (window instanceof Window && document instanceof Document)
    );
  } catch (__) {
    return false;
  }
})();

/**
 * Are we running in a worker?
 *
 * @returns true in dedicated workers and service workers
 **/
export const isWorker = (() => {
  try {
    return self instanceof WorkerGlobalScope;
  } catch (__) {
    return false;
  }
})();

/**
 * checks the existence of the frameio-desktop user agent
 * to determine if we're in an electron based desktop app.
 * frameio-desktop userAgent is set in the Electron main process.
 */
export const isDesktop = (): boolean => {
  try {
    return getGlobal().navigator.userAgent.includes('frameio-desktop');
  } catch (error) {
    return false;
  }
};

// Is Node.js? (probably server context of Next.js)
export const isServer = !isMainThread && !isWorker;

// Browser contexts
export const isClient = isMainThread || isWorker;

// Application is running in Adobe UXP
export const isUXPEnvironment = () => {
  return typeof window !== 'undefined' && window?.uxpHost !== undefined;
};

export const getGlobal = (): any => {
  if (typeof self !== 'undefined') {
    return self;
  }
  if (typeof window !== 'undefined') {
    return window;
  }
  if (typeof global !== 'undefined') {
    return global;
  }
  throw new Error('unable to locate global object');
};

/**
 * check the existence of Playwright in run-time to defer any invocation of
 * evaluation before it's required.
 */
export const isE2E = () => Boolean(getGlobal().__PLAYWRIGHT_GLOBAL__);

/**
 * Retrieves the user agent string from the global navigator object.
 * Will be null if server-rendered.
 *
 * @returns {string | null} The user agent string if available, otherwise null.
 */
export const getUserAgent = (): string | null => {
  const ua = getGlobal().navigator?.userAgent;
  return ua || null;
};
